<script setup>
// Kargo Kurtarma kart detayı — Şubede ve Dönüş Yolunda sekmelerinde ortak:
// varyant görselli ürün listesi + sipariş/varış şubesi bilgi şeridi + mini kargo
// hareket çubuğu + son nokta + iade geçmişi + Kişi Kartı / İade / Kargo Takip köprüleri.
// showJourney=false: çubuk zaten kartın üstünde görünüyorsa burada tekrarlanmaz.
import { computed } from 'vue';
import CargoJourneyBar from 'dashboard/components-next/CargoRescue/CargoJourneyBar.vue';

const props = defineProps({
  item: { type: Object, required: true },
  accountId: { type: [String, Number], required: true },
  showJourney: { type: Boolean, default: true },
});

const tl = v => (v || 0).toLocaleString('tr-TR');
const fmtReplyAt = t => {
  if (!t) return '';
  const d = new Date(t);
  return `${d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};
const STATUS_TR = {
  refunded: 'iade edildi',
  exchanged: 'değişim yapıldı',
  cancelled: 'iptal',
  code_issued: 'kod verildi',
  received: 'paket geldi',
  awaiting_iban: 'IBAN bekleniyor',
};

// Sürat'ın kendi takip sorgusu — takip numarası hazır dolu açılır.
const trackingUrl = computed(
  () =>
    `https://www.suratkargo.com.tr/KargoTakip/?kargotakipno=${props.item.tracking_no}`
);
</script>

<template>
  <div class="border-t border-n-weak bg-n-solid-2 px-4 py-3">
    <!-- MÜŞTERİ CEVABI ŞERİDİ — temsilci aramadan önce görür -->
    <div
      v-if="item.customer_reply"
      class="rounded-lg px-3 py-2 mb-2 text-sm font-semibold"
      :style="
        item.customer_reply === 'alacak'
          ? 'background:#dcfce7;color:#15803d'
          : 'background:#fee2e2;color:#b91c1c'
      "
    >
      💬 Müşteri cevabı:
      {{
        item.customer_reply === 'alacak'
          ? 'Teslim alacağım'
          : 'Teslim alamayacağım'
      }}
      <span v-if="item.customer_reply_at" class="font-normal">
        ({{ fmtReplyAt(item.customer_reply_at) }})</span
      >
      <template v-if="item.customer_reason">
        — “{{ item.customer_reason }}”
      </template>
    </div>

    <div
      v-for="(line, ix) in item.items || []"
      :key="ix"
      class="flex items-center gap-3 py-2 border-b border-n-weak last:border-0"
    >
      <img
        v-if="line.image"
        :src="line.image"
        class="w-12 h-16 object-cover rounded-md shrink-0"
        loading="lazy"
      />
      <div
        v-else
        class="w-12 h-16 rounded-md bg-n-slate-3 shrink-0 flex items-center justify-center text-n-slate-9"
      >
        🛍️
      </div>
      <div class="min-w-0 flex-1">
        <div class="font-semibold text-n-slate-12 text-sm truncate">
          {{ line.code }}
        </div>
        <div class="text-xs text-n-slate-11">{{ line.variant }}</div>
      </div>
      <div class="text-right shrink-0 text-sm">
        <div class="font-bold text-n-slate-12">× {{ line.qty }}</div>
        <div v-if="line.price" class="text-n-slate-11">
          {{ tl(line.price) }} ₺
        </div>
      </div>
    </div>
    <div
      v-if="!item.items || !item.items.length"
      class="text-sm text-n-slate-10 py-2"
    >
      Sipariş kalemi bulunamadı (takip no: {{ item.tracking_no }})
    </div>

    <!-- MİNİ KARGO HAREKET ÇUBUĞU (kartta zaten varsa tekrarlanmaz) -->
    <div v-if="showJourney" class="border-t border-n-weak mt-1 pt-3 pb-1">
      <CargoJourneyBar :item="item" />
    </div>

    <div
      class="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs text-n-slate-11 border-t border-n-weak mt-1 pt-2"
    >
      <div>
        <span class="text-n-slate-10">Sipariş:</span>
        {{ item.order_number || '—' }} ·
        {{ item.channel === 'ticimax' ? 'Koza' : 'ModaX' }}
      </div>
      <div>
        <span class="text-n-slate-10">Tarih:</span>
        {{ item.ordered_on || '—' }}
      </div>
      <div>
        <span class="text-n-slate-10">Varış şubesi:</span>
        {{ item.dest_branch || '—' }}
        <a
          v-if="item.dest_branch_phone"
          :href="`tel:${item.dest_branch_phone}`"
          class="font-semibold"
          style="color: #2563eb"
          >☎️ {{ item.dest_branch_phone }}</a
        >
        <template v-else-if="item.branch_phone">
          · {{ item.branch_phone }}
        </template>
      </div>
    </div>

    <div
      v-if="item.return_history && item.return_history.length"
      class="text-xs text-n-slate-11 border-t border-n-weak mt-2 pt-2"
    >
      <span class="font-bold text-n-slate-12">↩️ İade geçmişi:</span>
      <span v-for="h in item.return_history" :key="h.code" class="mr-2">
        {{ h.code }} ({{ h.on }} · {{ STATUS_TR[h.status] || h.status }})
      </span>
    </div>

    <div class="flex flex-wrap gap-2 mt-3">
      <router-link
        v-if="item.contact_id"
        :to="`/app/accounts/${accountId}/contacts/${item.contact_id}`"
        class="px-4 py-2 rounded-lg bg-n-slate-12 text-white font-bold text-sm"
      >
        👤 Kişi Kartı
      </router-link>
      <router-link
        :to="{
          name: 'return_center',
          query: { siparis: item.order_number || item.tracking_no },
        }"
        class="px-4 py-2 rounded-lg bg-woot-600 text-white font-bold text-sm"
      >
        ↩️ İade/Değişim'de aç
      </router-link>
      <a
        :href="trackingUrl"
        target="_blank"
        rel="noopener"
        class="px-4 py-2 rounded-lg text-white font-bold text-sm"
        style="background: #2563eb"
      >
        🚚 Kargo Takip
      </a>
      <router-link
        v-if="item.customer_conversation_id"
        :to="`/app/accounts/${accountId}/conversations/${item.customer_conversation_id}`"
        class="px-4 py-2 rounded-lg text-white font-bold text-sm"
        style="background: #0f766e"
      >
        💬 Konuşmayı aç
      </router-link>
    </div>
  </div>
</template>
